import { Component, EventEmitter, Input, Optional, Output, Self, ViewChild, ElementRef, AfterViewInit, OnChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { InputErrorMessages } from './disney.input.details';

@Component({
  selector: 'dtr-hub-disney-input',
  templateUrl: './disney-input.component.html',
  styleUrls: ['./disney-input.component.scss']
})
export class DisneyInputComponent implements OnChanges, ControlValueAccessor, AfterViewInit {

  valueControl = new FormControl();
  onChange: any = () => { };
  onTouched: any = () => { };
  private errorMessages = InputErrorMessages;

  @ViewChild('inputEl') inputEl: ElementRef;

  @Input() label: string;
  @Input() isRequired: boolean;
  @Input() maxLength = null;
  @Input() type: string;
  @Input() id: string;
  @Input() name: string;
  @Input() isError: boolean;
  @Input() errorMsgObj: object;
  @Input() showDollarSign: boolean;
  @Input() allowOnlyPositiveNumber = false;
  @Input() isNonEditable = false;
  @Input() isSearchable = true;
  @Input() helperText: string;
  @Input() displaySuccessIcon = false;
  @Input() successMsg: string;

  @Output() search = new EventEmitter();
  @Output() keyUp = new EventEmitter();
  @Output() inputValueChange = new EventEmitter();

  constructor(@Self() @Optional() public ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnChanges() {
    if (this.isNonEditable) {
      this.valueControl.disable();
    } else {
      this.valueControl.enable();
    }
  }

  ngAfterViewInit() {
    if (this.isSearchable) {
      fromEvent(this.inputEl.nativeElement, 'keyup')
      .pipe(debounceTime(700), distinctUntilChanged()).subscribe((event: any) => {
        this.search.emit(event.target.value);
      });
    }
  }

  writeValue(value: any): void {
    this.valueControl.setValue(value);
    this.onChange(value);
  }

  get errors(): Array<string> {
    if (!this.ngControl) { return []; }
    let { errors } = this.ngControl;

    if (this.errorMsgObj) {
      return Object.keys(errors).map(key => this.errorMsgObj[key] ? this.errorMsgObj[key] : <string>errors[key] || key);
    }

    return Object.keys(errors).map(key => this.errorMessages[key] ? this.errorMessages[key].replace('Field', this.label) : <string>errors[key] || key);
  }

  get invalid(): boolean {
    return this.ngControl ? this.ngControl.invalid : false;
  }

  get showError(): boolean {
    if (!this.ngControl) { return false; }
    const { dirty, touched } = this.ngControl;

    return (this.invalid) ? (dirty || touched) : false;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onControlValueChange(event: any) {
    event.target.value = event.target.value.trim();
    this.valueControl.patchValue(event.target.value);
    this.onChange(this.valueControl.value);
    this.inputValueChange.emit(this.valueControl.value);
  }

  handleKeyUp(event: any) {
    this.keyUp.emit(event);
  }

  onlyPositiveNumber(event: any) {
    if (this.allowOnlyPositiveNumber) {
      let code = event.charCode;

      return (code >= 48 && code <= 57) || code === 46;
    }
    if (this.type === 'number') {
      return event.charCode == 101 ? false : true;
    }
  }

  isDollarSignVisible() {
    return this.showDollarSign && this.isValuePresent();
  }

  resetValue() {
    this.valueControl.patchValue('');
    this.search.emit('');
    this.inputValueChange.emit('');
  }

  isValuePresent() {
    return this.valueControl && this.valueControl.value !== null && this.valueControl.value !== undefined && this.valueControl.value !== '';
  }
}
